import { useState } from "react";
import {
    Box,
    Button,
    TextField,
    useMediaQuery,
    Typography,
    Container,
    Grid,
    Snackbar,
    Alert
} from "@mui/material";
import { useFormik } from "formik";
import * as yup from "yup";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import logo from './img/logo.png';

// Validation Schema using Yup
const validationSchema = yup.object({
    email: yup.string()
        .email("Invalid email format")
        .required("Email is required"),
});

const ForgotPassword = () => {
    const navigate = useNavigate();
    const isNonMobile = useMediaQuery("(min-width:600px)");
    const isSmallScreen = useMediaQuery("(max-width:900px)");
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "info" });

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const formik = useFormik({
        initialValues: {
            email: "",
        },
        validationSchema,
        onSubmit: async (values, { setSubmitting, resetForm }) => {
            try {
                const url = `https://my-pers-thing-server.onrender.com/auth/password-reset`;
                const { data } = await axios.post(url, { email: values.email });
                setSnackbar({ open: true, message: data.message, severity: "success" });
                resetForm();
            } catch (error) {
                if (
                    error.response &&
                    error.response.status >= 400 &&
                    error.response.status <= 500
                ) {
                    setSnackbar({
                        open: true,
                        message: error.response.data.message,
                        severity: "error",
                    });
                } else {
                    setSnackbar({
                        open: true,
                        message: "Something went wrong",
                        severity: "error",
                    });
                }
            } finally {
                setSubmitting(false);
            }
        },
    });
    
    return (
        <Container maxWidth="xl" sx={{ 
            minHeight: '100vh', 
            display: 'flex', 
            flexDirection: 'column',
            pt: { xs: 2, md: 2 }
        }}>
            <style>
                {`
                    body {
                        margin: 0;
                        padding: 0;
                        overflow-x: hidden;
                    }
                `}
            </style>
            
            {/* Snackbar for showing error/success messages */}
            <Snackbar
                open={snackbar.open}
                autoHideDuration={7000}
                onClose={handleCloseSnackbar}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    onClose={handleCloseSnackbar}
                    severity={snackbar.severity}
                    sx={{
                        width: "100%",
                        fontSize: "16px",
                        borderRadius: "12px",
                        "& .MuiAlert-icon": {
                            fontSize: "24px",
                        },
                    }}
                >
                    {snackbar.message}
                </Alert>
            </Snackbar>
            
            {/* Header text section - moved up with reduced margins */}
            <Box sx={{ 
                textAlign: "center", 
                mt: { xs: 0, md: 1 },
                mb: { xs: 0, md: 1 }
            }}>
                <Typography
                    variant="h4"
                    sx={{
                        fontSize: { xs: "20px", sm: "22px", md: "25px" },
                        color: "#3B3B3B",
                        textTransform: "uppercase",
                        mb: 0.5
                    }}
                >
                    A Social Platform to Showcase & Discover Talent!
                </Typography>

                <Typography
                    variant="h6"
                    sx={{
                        fontSize: { xs: "16px", sm: "18px", md: "20px" },
                        color: "#555555",
                        fontWeight: "normal",
                    }}
                >
                    Singing 🎤, Rapping 🎶, Dancing 💃, Acting 🎭, Magic ✨, Comedy 😂, Art 🎨, Photography 📸, Instrumental 🎸, and more!
                </Typography>
            </Box>

            {/* Main content shifted upwards */}
            <Grid container spacing={6} sx={{ 
                flexGrow: 1, 
                alignItems: 'center',
                justifyContent: 'center',
                mt: { xs: -2, md: -3 }  // Reduced margin top to shift everything up
            }}>
                {/* Logo Column - Only visible on larger screens */}
                {!isSmallScreen && (
                    <Grid item xs={12} md={5} sx={{ 
                        display: 'flex', 
                        justifyContent: 'center',
                        mt: { xs: -2, md: -4 }  // Negative margin to shift up
                    }}>
                        <Box sx={{ maxWidth: '200px', width: '100%' }}>
                            <img 
                                src={logo}
                                alt="App Logo" 
                                style={{ width: '100%', height: 'auto', borderRadius: '10px' }} 
                            />
                        </Box>
                    </Grid>
                )}
                
                {/* Form Column */}
                <Grid item xs={12} sm={10} md={6} sx={{
                    mt: { xs: -3, md: -4 }  // Negative margin to shift up
                }}>
                    <form onSubmit={formik.handleSubmit}>
                        <Box
                            sx={{ 
                                width: '100%',
                                minWidth: { sm: '500px' },
                                maxWidth: '700px',
                                margin: '0 auto',
                                padding: { xs: '20px', sm: '30px' },
                                border: '1px solid #ddd',
                                borderRadius: '24px',
                                backgroundColor: '#fff'
                            }}
                        >
                            {/* Mobile logo display */}
                            {isSmallScreen && (
                                <Box sx={{ 
                                    display: 'flex', 
                                    justifyContent: 'center', 
                                    mb: 3,
                                    maxWidth: '100px',
                                    mx: 'auto'
                                }}>
                                    <img 
                                        src={logo}
                                        alt="App Logo" 
                                        style={{ width: '100%', height: 'auto', borderRadius: '10px' }} 
                                    />
                                </Box>
                            )}

                            <Typography 
                                variant="h5" 
                                fontWeight="bold" 
                                textAlign="center" 
                                sx={{ mb: 3 }}
                            >
                                Forgot Password
                            </Typography>

                            <Box
                                display="grid"
                                gap="20px"
                                gridTemplateColumns="repeat(4, minmax(0, 1fr))"
                            >
                                <TextField
                                    label={
                                        <>
                                            Email<span style={{ color: 'red' }}>*</span>
                                        </>
                                    }
                                    type="email"
                                    name="email"
                                    onBlur={formik.handleBlur}
                                    onChange={formik.handleChange}
                                    value={formik.values.email}
                                    error={Boolean(formik.touched.email) && Boolean(formik.errors.email)}
                                    helperText={formik.touched.email && formik.errors.email}
                                    sx={{
                                        gridColumn: "span 4",
                                        "& .MuiOutlinedInput-root": {
                                            borderRadius: "50px", 
                                            '& fieldset': {
                                                borderColor: '#ccc',
                                            },
                                            '&:hover fieldset': {
                                                borderColor: '#FF61D2',
                                            },
                                            '&.Mui-focused fieldset': {
                                                borderColor: '#FF61D2',
                                                borderWidth: '2px',
                                            },
                                        },
                                        "& .MuiInputLabel-root": {
                                            color: '#999',
                                        },
                                        "& .MuiInputLabel-root.Mui-focused": {
                                            color: '#FF61D2',
                                        },
                                    }}
                                />
                            </Box>
                            
                            {/* Buttons */}
                            <Box>
                                <Button
                                    fullWidth
                                    type="submit"
                                    disabled={formik.isSubmitting}
                                    sx={{
                                        m: "1.5rem 0",
                                        p: "0.75rem",
                                        background: "linear-gradient(135deg, #FF61D2 0%, #FE9090 100%)",
                                        color: "#fff",
                                        borderRadius: "50px",
                                        transition: "all 0.3s ease",
                                        "&:hover": {
                                            background: "linear-gradient(135deg, #FE9090 0%, #FF61D2 100%)",
                                            transform: "translateY(-2px)",
                                            boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                                        },
                                        "&.Mui-disabled": {
                                            background: "#ddd",
                                            color: "#999"
                                        }
                                    }}
                                >
                                    {formik.isSubmitting ? "Sending..." : "Send Reset Link"}
                                </Button>

                                <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                                    <Typography
                                        onClick={() => navigate("/login")}
                                        sx={{
                                            color: "#555555",
                                            fontSize: "14px",
                                            "&:hover": {
                                                cursor: "pointer",
                                                textDecoration: "underline",
                                            },
                                        }}
                                    >
                                        Back to Login
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </form>
                </Grid>
            </Grid>
        </Container>
    );
};

export default ForgotPassword;